import React, { useState, useRef, useEffect } from 'react';
import {
  Avatar,
  Skeleton,
  Box,
  SxProps,
  Theme,
  AvatarProps,
} from '@mui/material';
import { Person } from '@mui/icons-material';

/**
 * Props interface for the LazyLoadingImage component.
 * Extends MUI's AvatarProps while omitting 'src', 'onLoad', and 'onError' to provide custom loading behavior.
 */
export interface LazyLoadingImageProps
  extends Omit<AvatarProps, 'src' | 'onLoad' | 'onError'> {
  /**
   * The primary image source URL.
   * @example "https://example.com/avatar.jpg"
   */
  src?: string;

  /**
   * Fallback image URL that will be used if the primary src fails to load.
   * @example "https://example.com/default-avatar.jpg"
   */
  fallbackSrc?: string;

  /**
   * Alt text for the image, important for accessibility.
   * @default "Avatar"
   */
  alt?: string;

  /**
   * Size of the avatar. Can be a predefined size or custom pixel value.
   * - 'small': 32px
   * - 'medium': 40px
   * - 'large': 56px
   * - number: Custom pixel size
   * @default "medium"
   * @example "large" | 80
   */
  size?: number | 'small' | 'medium' | 'large';

  /**
   * Whether to show a skeleton loader while the image is loading.
   * @default true
   */
  showSkeleton?: boolean;

  /**
   * The variant of skeleton to show while loading.
   * Should match the intended avatar shape.
   * @default "circular"
   */
  skeletonVariant?: 'circular' | 'rectangular' | 'rounded';

  /**
   * Custom fallback icon or React node to show when all images fail to load.
   * @default <Person />
   * @example <PhotoCamera /> | <AccountCircle />
   */
  fallbackIcon?: React.ReactNode;

  /**
   * Callback function called when the image loads successfully.
   * @param none
   * @returns void
   */
  onLoad?: () => void;

  /**
   * Callback function called when the image fails to load.
   * @param error - The error event from the failed image load
   * @returns void
   */
  onError?: (error: Event | string) => void;

  /**
   * Custom styles to apply to the skeleton loader.
   * Uses MUI's SxProps for consistent styling.
   */
  skeletonSx?: SxProps<Theme>;

  /**
   * Whether to use Intersection Observer for lazy loading.
   * When false, images load immediately.
   * @default true
   */
  useIntersectionObserver?: boolean;

  /**
   * Root margin for the Intersection Observer.
   * Determines how far outside the viewport to start loading.
   * @default "50px"
   * @example "100px" | "0px 50px"
   */
  rootMargin?: string;

  /**
   * Threshold for the Intersection Observer (0-1).
   * Determines how much of the element must be visible to trigger loading.
   * @default 0.1
   * @example 0.5 (50% visible) | 1.0 (100% visible)
   */
  threshold?: number;
}

/** Mapping of predefined size names to pixel values */
const sizeMap = { small: 32, medium: 40, large: 56 };

/**
 * LazyLoadingImage - A React component that combines MUI's Avatar with lazy loading,
 * skeleton loaders, and fallback image functionality.
 *
 * @description
 * This component provides a complete avatar solution with the following features:
 * - **Lazy Loading**: Uses Intersection Observer API to load images only when they come into view
 * - **Skeleton Loading**: Shows MUI Skeleton placeholder while images are loading
 * - **Fallback Support**: Supports primary image → fallback image → fallback icon cascade
 * - **Performance**: Efficient viewport detection and memory management
 * - **Accessibility**: Maintains MUI Avatar accessibility features
 * - **Customization**: Full MUI Avatar prop compatibility with additional lazy loading options
 *
 * @example
 * ```tsx
 * // Basic usage
 * <LazyLoadingImage
 *   src="https://example.com/avatar.jpg"
 *   alt="User Avatar"
 *   size="medium"
 * />
 *
 * // With fallback and custom icon
 * <LazyLoadingImage
 *   src="https://primary.jpg"
 *   fallbackSrc="https://backup.jpg"
 *   fallbackIcon={<PhotoCamera />}
 *   size="large"
 * />
 *
 * // Custom styling and no lazy loading
 * <LazyLoadingImage
 *   src="/avatar.jpg"
 *   size={80}
 *   useIntersectionObserver={false}
 *   sx={{ border: '2px solid #1976d2' }}
 *   onLoad={() => console.log('Avatar loaded!')}
 * />
 * ```
 *
 * @param props - LazyLoadingImageProps extending MUI AvatarProps
 * @returns React functional component
 *
 * @version 1.0.0
 * @author Farmshare UI Components Team
 * @since 2025-09-22
 */
export const LazyLoadingImage: React.FC<LazyLoadingImageProps> = ({
  src,
  fallbackSrc,
  alt = 'Avatar',
  size = 32,
  showSkeleton = true,
  skeletonVariant = 'circular',
  fallbackIcon = <Person />,
  onLoad,
  onError,
  skeletonSx,
  useIntersectionObserver = true,
  rootMargin = '50px',
  threshold = 0.1,
  sx,
  ...avatarProps
}) => {
  // Component state for managing loading, visibility, and image sources
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>(
    'loading',
  );
  const [currentSrc, setCurrentSrc] = useState<string | undefined>(undefined);
  const [isInView, setIsInView] = useState(!useIntersectionObserver);
  const imgRef = useRef<HTMLDivElement>(null);
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);

  /** Calculate the final avatar size from props */
  const avatarSize = typeof size === 'number' ? size : sizeMap[size];

  /**
   * Set up Intersection Observer for lazy loading functionality.
   * Only initializes if lazy loading is enabled and the ref is available.
   * Automatically disconnects after the element comes into view.
   */
  useEffect(() => {
    if (!useIntersectionObserver || !imgRef.current || isInView) return;

    intersectionObserverRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          intersectionObserverRef.current?.disconnect();
        }
      },
      { rootMargin, threshold },
    );

    intersectionObserverRef.current.observe(imgRef.current);

    return () => {
      intersectionObserverRef.current?.disconnect();
    };
  }, [useIntersectionObserver, rootMargin, threshold]);

  /**
   * Handle image loading with fallback logic.
   * Creates a new Image object to preload the image and manage loading states.
   * Implements the cascade: primary image → fallback image → error state.
   */
  useEffect(() => {
    if (!isInView || !src || imageState === 'loaded') return;
    setImageState('loading');
    setCurrentSrc(src);

    const img = new Image();

    /** Handle successful image load */
    const handleLoad = () => {
      setImageState('loaded');
      onLoad?.();
    };

    /** Handle image load error with fallback logic */
    const handleError = (error: string | Event) => {
      if (fallbackSrc && currentSrc !== fallbackSrc) {
        // Try fallback image
        setCurrentSrc(fallbackSrc);
        setImageState('loaded');
      } else {
        setImageState('error');
        onError?.(error);
      }
    };

    img.onload = handleLoad;
    img.onerror = handleError;
    img.src = src;

    // Cleanup event listeners to prevent memory leaks
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [isInView, src, fallbackSrc, currentSrc, onLoad, onError]);

  /**
   * Reset component state when the src prop changes.
   * This ensures the component reloads when given a new image URL.
   */
  useEffect(() => {
    if (src) {
      setImageState('loading');
      setCurrentSrc(undefined);
    }
  }, [src]);

  /** Styles for the Avatar component */
  const avatarSx: SxProps<Theme> = {
    width: avatarSize,
    height: avatarSize,
    ...sx,
  };

  /** Styles for the Skeleton component */
  const skeletonSxProps: SxProps<Theme> = {
    width: avatarSize,
    height: avatarSize,
    ...skeletonSx,
  };

  // Render the Avatar component with loaded image or fallback icon
  return (
    <Box ref={imgRef}>
      {showSkeleton && imageState === 'loading' ? (
        <Skeleton
          variant={skeletonVariant}
          sx={skeletonSxProps}
          animation="wave"
        />
      ) : (
        <Avatar
          {...avatarProps}
          src={imageState === 'loaded' ? currentSrc : undefined}
          alt={alt}
          sx={avatarSx}
        >
          {imageState === 'error' && fallbackIcon}
        </Avatar>
      )}
    </Box>
  );
};

/**
 * Default export of the LazyLoadingImage component.
 * @default LazyLoadingImage
 */
export default LazyLoadingImage;
